import React, { useState } from 'react';
import { PublicNav, PublicFooter } from './founding/PublicChrome';
import { Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Keep in sync with /app/backend/terms_content.py
const TERMS_SECTIONS = [
  { num: '1', title: 'Service Overview', body: [
    'ScheduleOfWorks.com provides professional document preparation services for construction and property development projects. Our services include the preparation of Schedules of Works, Workmanship Specifications, and related tender documentation.',
  ]},
  { num: '2', title: 'Payment and Refunds', body: [
    ['2.1', 'Payment is required in full before work commences on your submission.'],
    ['2.2', 'Fees are non-refundable once work has commenced on your project.'],
    ['2.3', 'If you wish to cancel before work has commenced, please contact us immediately. Cancellation requests received after work has started will not be eligible for refunds.'],
    ['2.4', 'All prices displayed are inclusive of VAT where applicable.'],
    ['2.5', 'We reserve the right to review and cancel an order at our discretion. In these rare circumstances, a full refund will be issued and you will be notified within 24 hours of our decision.'],
  ]},
  { num: '3', title: 'Delivery Times', body: [
    ['3.1', 'Delivery dates provided are indicative and based on current workload at the time of order. Delivery times are calculated in working days only, excluding weekends and UK public holidays.'],
    ['3.2', 'Standard delivery is 7 working days. Priority delivery (5 working days) and Express delivery (3 working days) are available at additional cost.'],
    ['3.3', 'We reserve the right to extend delivery times with reasonable notice where circumstances require.'],
    ['3.4', 'If delivery is delayed beyond the agreed timeframe due to factors within our control, a discretionary discount may be offered on a future submission at our sole discretion.'],
    ['3.5', 'Delays caused by incomplete or unclear information provided by the client will not constitute a breach of delivery terms.'],
  ]},
  { num: '4', title: 'Client Responsibilities', body: [
    ['4.1', 'You are responsible for providing complete, accurate and legible information at the point of submission.'],
    ['4.2', 'Any revisions required due to missing or inaccurate information provided initially may incur additional charges.'],
    ['4.3', 'You confirm that you have the legal right to commission the work described in your submission.'],
  ]},
  { num: '5', title: 'Service Limitations', body: [
    ['5.1', 'Our Schedules of Works are prepared from the information provided in your submission. They are a document preparation service and are not a substitute for professional inspection, valuation, or on-site survey.'],
    ['5.2', 'Cost estimates where provided are indicative and based on professional quantity surveying principles. They do not constitute a binding quote or professional valuation.'],
    ['5.3', 'We do not provide site visits, structural surveys, measurements, or contractor management as part of our standard service.'],
  ]},
  { num: '6', title: 'Intellectual Property', body: [
    ['6.1', 'All documents produced by ScheduleOfWorks.com remain our intellectual property until payment is received in full.'],
    ['6.2', 'Upon full payment, you receive a licence to use the documents for the specific project described in your submission.'],
    ['6.3', 'You may not resell, redistribute, or use our documents as templates for third-party projects without written consent.'],
  ]},
  { num: '7', title: 'Confidentiality', body: [
    ['7.1', 'We treat all client information as confidential and will not share it with third parties without consent, except where required by law.'],
    ['7.2', 'We may use anonymised project data for internal training and service improvement purposes.'],
  ]},
  { num: '8', title: 'Liability', body: [
    ['8.1', 'Our liability is limited to the fee paid for the specific service. We are not liable for any indirect, consequential, or incidental losses arising from the use of our documents.'],
    ['8.2', 'It is your responsibility to review all documents and raise any queries within 5 working days of delivery. Complaints raised after this period may not be actioned.'],
    ['8.3', 'We do not warrant that our documents will achieve any specific outcome, including but not limited to planning approval, cost savings, or contractor selection.'],
  ]},
  { num: '9', title: 'Complaints and Revisions', body: [
    ['9.1', 'Minor corrections and clarifications are provided free of charge within 5 working days of delivery.'],
    ['9.2', 'Substantive revisions or additions requested after delivery will be treated as a new submission and charged accordingly.'],
    ['9.3', 'Any formal complaint should be sent in writing to team@schedulesofwork.com within 10 working days of delivery. We will respond within 5 working days.'],
  ]},
  { num: '10', title: 'Force Majeure', body: [
    ['10.1', 'We shall not be liable for any failure or delay in performance caused by events beyond our reasonable control, including but not limited to acts of God, pandemics, power outages, internet disruptions, or supplier failures.'],
  ]},
  { num: '11', title: 'Data Protection', body: [
    ['11.1', 'We process personal data in accordance with applicable data protection laws.'],
    ['11.2', 'Information provided will be used solely for the purpose of delivering our services and will not be shared with third parties without consent, except where required by law.'],
  ]},
  { num: '12', title: 'Governing Law', body: [
    ['12.1', 'These terms and conditions are governed by the laws of England and Wales.'],
    ['12.2', 'Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of England and Wales.'],
  ]},
  { num: '13', title: 'Amendments', body: [
    ['13.1', 'We reserve the right to amend these terms and conditions at any time.'],
    ['13.2', 'The terms applicable to your order are those in force at the time of submission.'],
  ]},
  // ── Phase E additions (19 Apr 2026) ──
  { num: '14', title: 'Technology Disclosure', body: [
    'This platform uses SCOPEai, an AI-powered system, to generate cost estimates and project documents. Outputs are AI-generated and may contain errors. All outputs are indicative only and must be verified by a qualified professional before being relied upon for financial decisions, procurement or contract purposes.',
  ]},
  { num: '15', title: 'Scope of Use', body: [
    'This platform is designed for straightforward residential refurbishment only. It is not suitable for projects requiring building regulations full plans approval, JCT contracts, party wall awards, listed building consent or any other statutory consent. For projects of this nature contact Macventure Consultancy via the managed service.',
  ]},
  { num: '16', title: 'Subscription Terms', body: [
    'Pro and Pro+ subscriptions are billed annually. Monthly download and call allowances reset on the billing anniversary date and do not roll over. On cancellation, access continues until the end of the current billing period. If a renewal payment fails a 7-day grace period applies before the account reverts to the free tier. Sessions and documents remain accessible as read-only on revert to free. Accounts above the free session limit become read-only and are not deleted.',
  ]},
  { num: '17', title: 'Data Retention', body: [
    'Free accounts: Project data and documents are retained for 180 days from the date of creation. You will receive an email reminder 30 days before your data is due to expire.',
    'Pro and Pro+ accounts: Project data and documents are retained for the duration of your active subscription plus 90 days following cancellation or expiry. Documents are retained for a minimum of 12 months from the date of creation. You will receive an email reminder 30 days before any data is due to expire.',
    'Marketing consent records are retained for 2 years from the date of consent in accordance with UK GDPR requirements.',
    'You may request deletion of your data at any time by contacting support@schedulesofwork.com. Deletion requests will be processed within 30 days in accordance with your rights under UK GDPR.',
  ]},
  { num: '18', title: 'Contact', body: [
    'For any questions regarding these terms and conditions, please contact us at team@schedulesofwork.com.',
  ]},
];

const Section = ({ num, title, body }) => (
  <section className="card" data-testid={`terms-section-${num}`}>
    <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--color-navy)' }}>
      {num}. {title}
    </h2>
    <div className="space-y-3" style={{ color: 'var(--color-muted)' }}>
      {body.map((item, i) =>
        Array.isArray(item) ? (
          <p key={i}>
            <strong style={{ color: 'var(--color-text)' }}>{item[0]}</strong> {item[1]}
          </p>
        ) : (
          <p key={i} className="leading-relaxed">{item}</p>
        )
      )}
    </div>
  </section>
);

export const TermsPage = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Stream the PDF via a hidden link so the browser handles the file save dialog
      const a = document.createElement('a');
      a.href = `${API}/terms/pdf`;
      a.download = ''; // let server set filename via Content-Disposition
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setTimeout(() => setDownloading(false), 1500);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-bg)' }}>
      <PublicNav cta="home" />

      <main className="pt-28 pb-20" data-testid="terms-main-content">
        <div className="content-container max-w-4xl">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
            <h1 style={{ color: 'var(--color-navy)', margin: 0 }} data-testid="terms-title">
              Terms and Conditions
            </h1>
            <button
              onClick={handleDownload}
              disabled={downloading}
              data-testid="download-terms-pdf-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: '#0F2742', color: '#fff', border: 'none',
                padding: '10px 18px', borderRadius: 9, fontSize: 13, fontWeight: 700,
                cursor: downloading ? 'wait' : 'pointer', opacity: downloading ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              <Download size={15} />
              {downloading ? 'Opening…' : 'Download Terms and Conditions (PDF)'}
            </button>
          </div>

          <div className="space-y-6">
            {TERMS_SECTIONS.map((s) => (
              <Section key={s.num} num={s.num} title={s.title} body={s.body} />
            ))}

            <div className="pt-4" style={{ borderTop: '1px solid var(--color-border)' }}>
              <p className="text-sm" style={{ color: 'var(--color-muted)' }} data-testid="terms-last-updated">
                Last updated: April 2026 · Version 2026.04.19
              </p>
            </div>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
};

export default TermsPage;
